import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import MerkezSecici from '@/components/MerkezSecici';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Pencil, Save, X, Upload, Trash2, Plus, Check, Clock, FileText, User, Briefcase, Calendar, CreditCard, AlertTriangle, Phone, Mail, MapPin, Shield, Building2, Star, BadgeCheck, Search, PlusCircle, ChevronDown, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { panoyaKopyala } from '@/lib/pano';
import WhatsAppDugmesi from '@/components/WhatsAppDugmesi';
import WhatsAppGonder from '@/components/WhatsAppGonder';
import { trNorm } from '@/lib/arama';
import { useAuth } from '@/contexts/AuthContext';
import { YeniIzinDialog, TalepTablosu, BakiyeOzeti } from './izinOrtak';

const empTypes = { full_time: 'Tam Zamanli', part_time: 'Yari Zamanli', contractor: 'Sozlesmeli' };
const genders = { male: 'Erkek', female: 'Kadin' };
const docTypes = { id_copy: 'Kimlik', residence: 'Ikametgah', health_report: 'Saglik Raporu', contract: 'Sozlesme', insurance_registration: 'SGK', criminal_record: 'Sabika Kaydi', cv: 'CV', diploma: 'Diploma', other: 'Diger' };
const statusColors = { pending: 'bg-[#7a6a2e]/10 text-[#7a6a2e]', approved: 'bg-[#3d6e4e]/10 text-[#3d6e4e]', rejected: 'bg-[#7a3535]/10 text-[#7a3535]', draft: 'bg-[#e8dfd0] text-[#3F3E39]', paid: 'bg-[#2e2d29]/10 text-[#2e2d29]' };
const statusLabels = { pending: 'Bekliyor', approved: 'Onaylandi', rejected: 'Reddedildi', draft: 'Taslak', paid: 'Odendi' };
const months = ['Ocak','Subat','Mart','Nisan','Mayis','Haziran','Temmuz','Agustos','Eylul','Ekim','Kasim','Aralik'];

// ─── HİZMET DÖNEMLERİ (yardımcılar) ───
// Sunucudaki `_donemler` ile aynı kural: alan yoksa eski iki alandan türetilir,
// böylece dönem bilgisi olmayan ESKİ kayıtlar da tek dönemli görünür.
const donemListesi = (emp) => {
  const ham = emp?.donemler;
  if (Array.isArray(ham) && ham.length) return ham.filter(d => d?.giris);
  if (!emp?.hire_date) return [];
  return [{ giris: emp.hire_date, cikis: emp.termination_date || null }];
};

const trTarih = (iso) => {
  const s = String(iso || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s || '—';
  const [y, a, g] = s.split('-');
  return `${g}.${a}.${y}`;
};

// Süre TAKVİMSEL hesaplanır (gün sayısını 30'a bölmek 31 günlük ayları yutuyordu)
const donemSuresi = (giris, cikis) => {
  if (!giris) return '—';
  const bas = new Date(giris);
  const bit = cikis ? new Date(cikis) : new Date();
  if (isNaN(bas) || isNaN(bit) || bit < bas) return '—';
  // Giriş ve çıkış günü de çalışılmış sayılır: kapsayıcı süre, bitişin
  // ERTESİ gününe olan takvim farkıdır (1–31 Ocak = 1 ay, 30 gün değil).
  const son = new Date(bit.getFullYear(), bit.getMonth(), bit.getDate() + 1);
  // Ay eklerken AYIN SON GÜNÜNE kırpılır: 31 Ocak + 1 ay = 28 Şubat, JS'in
  // kendi taşması (3 Mart) ay sayısını bir fazla gösterip günü EKSİYE düşürüyordu.
  const ayEkle = (d, n) => {
    const y = d.getFullYear(), a = d.getMonth() + n;
    return new Date(y, a, Math.min(d.getDate(), new Date(y, a + 1, 0).getDate()));
  };
  let aylar = (son.getFullYear() - bas.getFullYear()) * 12 + (son.getMonth() - bas.getMonth());
  if (ayEkle(bas, aylar) > son) aylar -= 1;
  const gun = Math.round((son - ayEkle(bas, aylar)) / 86400000);
  const parca = [];
  if (Math.floor(aylar / 12)) parca.push(`${Math.floor(aylar / 12)} yıl`);
  if (aylar % 12) parca.push(`${aylar % 12} ay`);
  if (gun || !parca.length) parca.push(`${gun} gün`);
  return parca.join(' ');
};

export default function EmployeeDetail() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('employees', 'delete');
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/hr/employees');
  const [emp, setEmp] = useState(null);
  usePageTitle(emp?.full_name || 'Personel Detayı');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  // Merkez listesi burada da lazım: görüntüleme modunda personelin merkezinin
  // ADI yazılıyor, `MerkezSecici` yalnızca düzenleme modunda basılıyor.
  const [merkezler, setMerkezler] = useState([]);

  // Tab data
  const [docs, setDocs] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [payroll, setPayroll] = useState([]);

  // Dialogs
  const [showDoc, setShowDoc] = useState(false);
  const [showSalary, setShowSalary] = useState(false);
  const [showAdvance, setShowAdvance] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAll();
    api.get('/sorumluluk-merkezleri').then(r => setMerkezler(r.data || [])).catch(() => {});
    api.get('/hr/departments').then(r => setDepartments(r.data)).catch(() => {});
    api.get('/hr/positions').then(r => setPositions(r.data)).catch(() => {});
  }, [id]);

  const fetchAll = () => {
    api.get(`/hr/employees/${id}`).then(r => { setEmp(r.data); setEditForm(r.data); }).catch(() => navigate('/erp/hr/employees'));
    api.get(`/hr/employees/${id}/documents`).then(r => setDocs(r.data)).catch(() => {});
    api.get(`/hr/employees/${id}/salary`).then(r => setSalaries(r.data)).catch(() => {});
    api.get(`/hr/advances?employee_id=${id}`).then(r => setAdvances(r.data)).catch(() => {});
    api.get(`/hr/payroll`).then(r => setPayroll(r.data.filter(p => p.employee_id === id))).catch(() => {});
  };

  const saveEdit = async () => {
    setLoading(true);
    try {
      await api.patch(`/hr/employees/${id}`, editForm);
      toast.success('Kaydedildi');
      setEditing(false);
      fetchAll();
    } catch (err) { toast.error('Hata'); }
    setLoading(false);
  };

  // İşe dönüş tarihi TARİH SEÇİCİYLE alınır: `window.prompt` biçimi
  // kullanıcıya bırakıyordu ve yanlış yazılan tarih sessizce 400 dönüyordu.
  const [iseDonusAcik, setIseDonusAcik] = useState(false);
  const [iseDonusTarihi, setIseDonusTarihi] = useState('');
  const [iseDonusKayit, setIseDonusKayit] = useState(false);

  const iseDonusuAc = () => {
    setIseDonusTarihi(new Date().toLocaleDateString('en-CA'));
    setIseDonusAcik(true);
  };

  const iseDondur = async () => {
    if (!iseDonusTarihi) return;
    setIseDonusKayit(true);
    try {
      await api.post(`/hr/employees/${id}/ise-don`, { giris: iseDonusTarihi });
      toast.success('Personel yeni bir hizmet dönemiyle işe başlatıldı');
      setIseDonusAcik(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'İşlem başarısız');
    }
    setIseDonusKayit(false);
  };

  const deleteEmployee = async () => {
    if (!window.confirm(`${fullName} adlı personeli ve bağlı cari hesabını pasife almak istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/hr/employees/${id}`);
      toast.success('Personel pasife alındı');
      navigate('/erp/hr/employees');
    } catch { toast.error('Hata'); }
  };

  if (!emp) return <div className="text-center py-8 text-[#8a7e70]">Yukleniyor...</div>;
  const fullName = `${emp.first_name} ${emp.last_name}`;
  // Yeni giriş önceki çıkıştan sonra olmalı; tarih seçicinin alt sınırı
  const donemler = donemListesi(emp);
  const sonCikis = donemler.length ? (donemler[donemler.length - 1].cikis || '') : '';
  const deptName = departments.find(d => d.id === emp.department_id)?.name || '-';
  const posName = positions.find(p => p.id === emp.position_id)?.name || '-';


  return (
    <div data-testid="employee-detail-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]" data-testid="back-btn"><ArrowLeft size={18} /></Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#2e2d29]/10 flex items-center justify-center"><User size={24} className="text-[#2e2d29]" /></div>
            <div>
              <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">{fullName}</h1>
              <p className="text-sm text-[#8a7e70]">{deptName} — {posName}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!emp.is_active && <span className="status-badge bg-[#7a3535]/10 text-[#7a3535]">Pasif</span>}
          {/* Ayrilan personel YENI BIR HIZMET DONEMIYLE geri alinir: yeni kart
              acmak sicili ve gecmisi koparirdi, cikis tarihini silmek de
              ayri kaldigi donemi "calismis" gosterirdi (2026-09-19). */}
          {!emp.is_active && (
            <Button variant="outline" size="sm" onClick={iseDonusuAc}
              className="border-[#d0c4b0] text-[#3d6e4e] hover:bg-[#3d6e4e]/10"
              data-testid="ise-don">
              <Check size={14} className="mr-1" /> Tekrar İşe Başlat
            </Button>
          )}
          {emp.is_active && silmeYetkisi && (
            <Button variant="outline" size="sm" onClick={deleteEmployee} className="border-[#d0c4b0] text-[#7a3535] hover:bg-[#7a3535]/10">
              <Trash2 size={14} className="mr-1" /> Pasife Al
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue={searchParams.get('sekme') === 'izin' ? 'leaves' : 'info'} className="w-full">
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="info">Özlük</TabsTrigger>
          <TabsTrigger value="docs">Evraklar</TabsTrigger>
          <TabsTrigger value="salary">Maaş ve Dönemler</TabsTrigger>
          <TabsTrigger value="leaves">İzinler</TabsTrigger>
          <TabsTrigger value="advances">Avanslar</TabsTrigger>
          <TabsTrigger value="payroll">Bordro</TabsTrigger>
        </TabsList>

        {/* ── SEKME 1: OZLUK ── */}
        <TabsContent value="info">
          <FormLinkKarti empId={id} emp={emp} onGuncellendi={fetchAll} />
          <OzlukTab
            emp={emp} editing={editing} editForm={editForm} setEditForm={setEditForm}
            departments={departments} positions={positions} merkezler={merkezler}
            setDepartments={setDepartments} setPositions={setPositions}
            deptName={deptName} posName={posName} fullName={fullName}
            loading={loading}
            onEdit={() => { setEditing(true); setEditForm({...emp}); }}
            onCancel={() => setEditing(false)}
            onSave={saveEdit}
          />
        </TabsContent>

        {/* ── SEKME 2: EVRAKLAR ── */}
        <TabsContent value="docs">
          <DocTab empId={id} docs={docs} onRefresh={fetchAll} />
        </TabsContent>

        {/* ── SEKME 3: MAAS VE DONEMLER ──
            Donemler burada: Ozluk'teki Is Bilgileri kartinin dibinde kaliyordu
            ve kullanici isten donusten sonra donemleri goremiyordu (2026-09-19). */}
        <TabsContent value="salary" className="space-y-6">
          <DonemKarti emp={emp} onIseDon={iseDonusuAc} />
          <SalaryTab empId={id} salaries={salaries} onRefresh={fetchAll} />
        </TabsContent>

        {/* ── SEKME 4: IZINLER ── */}
        <TabsContent value="leaves">
          <IzinTab empId={id} emp={emp} />
        </TabsContent>

        {/* ── SEKME 5: AVANSLAR ── */}
        <TabsContent value="advances">
          <SimpleList title="Avanslar" data={advances} columns={[
            { key: 'request_date', label: 'Tarih' },
            { key: 'amount', label: 'Tutar', align: 'right', render: (v, r) => `${v?.toFixed(2)} ${r.currency}` },
            { key: 'description', label: 'Aciklama' },
            { key: 'status', label: 'Durum', render: v => <span className={`status-badge ${statusColors[v]}`}>{statusLabels[v]}</span> }
          ]} />
        </TabsContent>

        {/* ── SEKME 6: BORDRO ── */}
        <TabsContent value="payroll">
          <SimpleList title="Bordro Gecmisi" data={payroll} columns={[
            { key: 'period', label: 'Donem', render: (_, r) => `${months[(r.period_month||1)-1]} ${r.period_year}` },
            { key: 'net_salary', label: 'Net Maas', align: 'right', render: (v, r) => `${v?.toFixed(2)} ${r.currency}` },
            { key: 'total_net', label: 'Toplam', align: 'right', render: (v, r) => `${v?.toFixed(2)} ${r.currency}` },
            { key: 'status', label: 'Durum', render: v => <span className={`status-badge ${statusColors[v]}`}>{statusLabels[v]}</span> }
          ]} />
        </TabsContent>
      </Tabs>

      {/* İŞE DÖNÜŞ — yeni hizmet dönemi açar */}
      <Dialog open={iseDonusAcik} onOpenChange={setIseDonusAcik}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916]">Tekrar İşe Başlat</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#6b6255]">
              <span className="font-semibold text-[#1a1916]">{fullName}</span> için
              yeni bir hizmet dönemi açılacak. Önceki dönem
              {sonCikis ? <> (<span className="font-mono">{trTarih(sonCikis)}</span> çıkışlı)</> : null} kayıtlarda kalır.
            </p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-[#8a7e70]">
                Yeni İşe Giriş Tarihi
              </Label>
              <Input type="date" value={iseDonusTarihi} min={sonCikis || undefined}
                onChange={e => setIseDonusTarihi(e.target.value)}
                className="mt-1 bg-white border-[#d0c4b0]" data-testid="ise-don-tarih" />
            </div>
            <div className="text-xs text-[#6b6255] bg-[#7a6a2e]/8 border border-[#7a6a2e]/20 rounded-md p-3 space-y-1">
              <p>• İki dönem arasındaki günler puantajda <b>kadro dışı</b> sayılır, o günler için ücret hesaplanmaz.</p>
              <p>• <b>Kıdem bu tarihten yeniden başlar</b>; yıllık izin hak edişi sıfırdan işler.</p>
              <p>• Geçmiş bordro, evrak ve izin kayıtları aynı personel kartında durur.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIseDonusAcik(false)}
              className="border-[#d0c4b0]">Vazgeç</Button>
            <Button onClick={iseDondur} disabled={!iseDonusTarihi || iseDonusKayit}
              className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e]" data-testid="ise-don-kaydet">
              {iseDonusKayit ? 'Kaydediliyor…' : 'İşe Başlat'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── HİZMET DÖNEMLERİ KARTI ───
// Tek dönemli personelde de gösterilir: "dönem" kavramı ancak görünürse
// anlaşılıyor, ayrıca işten çıkışın nereden girildiği belli oluyor.
function DonemKarti({ emp, onIseDon }) {
  const donemler = donemListesi(emp);
  const acik = donemler.length > 0 && !donemler[donemler.length - 1].cikis;
  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="pb-3 border-b border-[#e8dfd0]">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#7a6a2e]/8 flex items-center justify-center">
              <Calendar size={14} className="text-[#7a6a2e]" strokeWidth={1.8} />
            </div>
            <CardTitle className="text-sm font-bold text-[#1a1916]">Hizmet Dönemleri</CardTitle>
          </div>
          {!acik && (
            <Button size="sm" variant="outline" onClick={onIseDon}
              className="border-[#d0c4b0] text-[#3d6e4e] hover:bg-[#3d6e4e]/10"
              data-testid="ise-don-donem">
              <Check size={13} className="mr-1" /> Tekrar İşe Başlat
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {donemler.length === 0 ? (
          <p className="text-sm text-[#8a7e70]">
            İşe giriş tarihi girilmemiş — Özlük sekmesinden düzenleyin.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="hizmet-donemleri">
                <thead>
                  <tr className="border-b border-[#e8dfd0] text-left">
                    <th className="py-1.5 pr-3 text-[10px] font-bold uppercase text-[#8a7e70] w-10">#</th>
                    <th className="py-1.5 pr-3 text-[10px] font-bold uppercase text-[#8a7e70]">İşe Giriş</th>
                    <th className="py-1.5 pr-3 text-[10px] font-bold uppercase text-[#8a7e70]">İşten Çıkış</th>
                    <th className="py-1.5 pr-3 text-[10px] font-bold uppercase text-[#8a7e70]">Süre</th>
                    <th className="py-1.5 text-[10px] font-bold uppercase text-[#8a7e70]">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {donemler.map((d, i) => (
                    <tr key={i} className="border-b border-[#e8dfd0]/60 last:border-0">
                      <td className="py-2 pr-3 text-[#8a7e70] font-mono text-xs">{i + 1}</td>
                      <td className="py-2 pr-3 font-mono text-xs text-[#1a1916]">{trTarih(d.giris)}</td>
                      <td className="py-2 pr-3 font-mono text-xs text-[#1a1916]">
                        {d.cikis ? trTarih(d.cikis) : <span className="text-[#c0b8ad]">—</span>}
                      </td>
                      <td className="py-2 pr-3 text-xs text-[#6b6255]">{donemSuresi(d.giris, d.cikis)}</td>
                      <td className="py-2">
                        {d.cikis
                          ? <span className="status-badge bg-[#8a7e70]/10 text-[#6b6255]">Kapandı</span>
                          : <span className="status-badge bg-[#3d6e4e]/10 text-[#3d6e4e]">Devam ediyor</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <dl className="mt-4 pt-3 border-t border-[#e8dfd0] grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <InfoRow label="İlk İşe Giriş" value={trTarih(donemler[0].giris)} />
              <InfoRow label="Kıdem Başlangıcı"
                value={trTarih(emp.kidem_baslangici || donemler[donemler.length - 1].giris)} />
            </dl>
            {donemler.length > 1 && (
              <p className="text-[11px] text-[#8a7e70] mt-3">
                Dönemler arasındaki günler puantajda kadro dışı sayılır; kıdem ve yıllık
                izin hak edişi <b>son dönemin girişinden</b> hesaplanır. Tarih düzeltmek
                için Özlük sekmesindeki Düzenle'yi kullanın — oradaki giriş/çıkış alanları
                <b> son dönemi</b> günceller.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Ozluk Tab ──
// ── PERSONEL BILGI FORMU LINKI ──
// Personel kendi kisisel/iletisim/banka bilgilerini telefonundan doldursun diye
// girissiz bir link uretilir (WhatsApp'tan gonderilir). Link TEK KULLANIMLIK:
// personel gonderdigi anda kapanir. Duzeltme gerekirse yetkili ya ayni linkin
// kilidini acar ya da yenisini uretir. Kurallarin tamami sunucuda
// (`hr.py` → PERSONEL_FORM_ALANLARI); burasi yalnizca yonetim ekrani.
function FormLinkKarti({ empId, emp, onGuncellendi }) {
  const [durum, setDurum] = useState(null);
  const [islemde, setIslemde] = useState(false);

  const getir = useCallback(async () => {
    try {
      const r = await api.get(`/hr/employees/${empId}/form-link`);
      setDurum(r.data);
    } catch { setDurum(null); }
  }, [empId]);

  useEffect(() => { getir(); }, [getir]);

  const linkAdresi = durum?.token
    ? `${window.location.origin}/personel-formu/${durum.token}` : '';

  const cagir = async (yol, yontem = 'post', basari = '') => {
    setIslemde(true);
    try {
      const r = await api[yontem](`/hr/employees/${empId}/form-link${yol}`);
      setDurum(r.data);
      if (basari) toast.success(basari);
      onGuncellendi?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'İşlem tamamlanamadı');
    }
    setIslemde(false);
  };

  const kopyala = async () => {
    try {
      await navigator.clipboard.writeText(linkAdresi);
      toast.success('Bağlantı kopyalandı — WhatsApp\'tan yapıştırabilirsiniz');
    } catch {
      // clipboard API guvenli olmayan baglamda (http) calismiyor; elle secim
      // yapilabilsin diye alani isaretliyoruz.
      document.querySelector('[data-testid="form-link-adres"]')?.select();
      toast.info('Kopyalanamadı — bağlantıyı seçip elle kopyalayın');
    }
  };

  if (!durum) return null;

  return (
    <Card className="border-[#d0c4b0] mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#1a1916] flex items-center gap-2">
          <FileText size={15} className="text-[#4a5a3a]" />
          Personel Bilgi Formu
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {!durum.var ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-[#6b5f4f]">
              Personelin kişisel, iletişim ve banka bilgilerini kendi telefonundan
              doldurması için bağlantı oluşturun.
            </p>
            <Button size="sm" disabled={islemde}
              onClick={() => cagir('', 'post', 'Bağlantı oluşturuldu')}
              className="bg-[#4a5a3a] hover:bg-[#3d4a30] text-white text-xs h-8"
              data-testid="form-link-olustur">
              <PlusCircle size={13} className="mr-1.5" /> Bağlantı Oluştur
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center gap-2 flex-wrap text-xs">
              {durum.iptal ? (
                <span className="status-badge bg-[#7a3535]/10 text-[#7a3535]">İptal edildi</span>
              ) : durum.dolduruldu ? (
                <span className="status-badge bg-[#3d6e4e]/10 text-[#3d6e4e]">
                  Dolduruldu — bağlantı kapandı
                </span>
              ) : (
                <span className="status-badge bg-[#7a6a2e]/10 text-[#7a6a2e]">
                  Doldurulmayı bekliyor
                </span>
              )}
              {durum.doldurulma_zamani && (
                <span className="text-[#8a7e70]">
                  {new Date(durum.doldurulma_zamani).toLocaleString('tr-TR')}
                </span>
              )}
            </div>

            {durum.acilabilir && (
              <div className="flex items-center gap-1.5">
                <input readOnly value={linkAdresi} data-testid="form-link-adres"
                  onFocus={e => e.target.select()}
                  className="flex-1 min-w-0 h-8 text-xs rounded-md border border-[#d0c4b0] bg-[#f5f1e8] px-2 text-[#3F3E39]" />
                <Button size="sm" variant="outline" onClick={kopyala}
                  className="text-xs h-8 border-[#d0c4b0]" data-testid="form-link-kopyala">
                  Kopyala
                </Button>
                <WhatsAppGonder modul="personel_formu" kayitId={empId} etiket="WhatsApp"
                  className="text-xs h-8" testId="form-link-whatsapp" />
              </div>
            )}

            <div className="flex items-center gap-1.5 flex-wrap">
              {(durum.dolduruldu || durum.iptal) && (
                <Button size="sm" variant="ghost" disabled={islemde}
                  onClick={() => cagir('/unlock', 'post', 'Bağlantının kilidi açıldı')}
                  className="text-xs h-7 text-[#3d6e4e] hover:bg-[#3d6e4e]/10"
                  data-testid="form-link-kilit-ac">
                  <Check size={13} className="mr-1" /> Kilidi Aç (aynı bağlantı)
                </Button>
              )}
              <Button size="sm" variant="ghost" disabled={islemde}
                onClick={() => cagir('', 'post', 'Yeni bağlantı oluşturuldu — eskisi geçersiz')}
                className="text-xs h-7 text-[#6b5f4f] hover:bg-[#6b5f4f]/10"
                data-testid="form-link-yenile">
                Yeni Bağlantı
              </Button>
              {!durum.iptal && (
                <Button size="sm" variant="ghost" disabled={islemde}
                  onClick={() => { if (window.confirm('Bağlantı iptal edilsin mi? Personel bir daha açamaz.')) cagir('', 'delete', 'Bağlantı iptal edildi'); }}
                  className="text-xs h-7 text-[#7a3535] hover:bg-[#7a3535]/10"
                  data-testid="form-link-iptal">
                  <X size={13} className="mr-1" /> İptal Et
                </Button>
              )}
            </div>

            {emp?.form_onceki_degerler && Object.keys(emp.form_onceki_degerler).length > 0 && (
              <p className="text-[11px] text-[#8a7e70]">
                Personel {Object.keys(emp.form_onceki_degerler).length} alanı değiştirdi.
                Önceki değerler kayıtta saklanıyor.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OzlukTab({ emp, editing, editForm, setEditForm, departments, positions, merkezler, setDepartments, setPositions, deptName, posName, fullName, loading, onEdit, onCancel, onSave }) {
  // Ayrilip geri donmus personel: tarih alanlarinin anlami degisiyor
  const cokDonemli = (emp.donemler || []).length > 1;

  const createDepartment = async (name) => {
    const res = await api.post('/hr/departments', { name });
    const newDept = { value: res.data.id, label: res.data.name };
    setDepartments(prev => [...prev, res.data]);
    toast.success(`"${name}" departmanı eklendi`);
    return newDept;
  };

  const createPosition = async (name) => {
    const res = await api.post('/hr/positions', { name, department_id: editForm.department_id || null });
    const newPos = { value: res.data.id, label: res.data.name };
    setPositions(prev => [...prev, res.data]);
    toast.success(`"${name}" pozisyonu eklendi`);
    return newPos;
  };
  const initials = [emp.first_name?.[0], emp.last_name?.[0]].filter(Boolean).join('').toUpperCase() || '?';
  const activeSalary = null; // could be passed as prop if needed

  return (
    <div className="space-y-4 animate-fade-in">

      {/* ── Profil Kartı ── */}
      <Card className="bg-[#faf7f2] border-[#d0c4b0] overflow-hidden">
        <div className="h-1.5 w-full bg-gradient-to-r from-[#2e2d29] via-[#3d6e4e] to-[#7a6a2e]" />
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-5">
              {/* Avatar */}
              <div className="w-16 h-16 rounded-2xl bg-[#2e2d29] flex items-center justify-center flex-shrink-0 shadow-md">
                <span className="text-[#f5f0e8] font-extrabold text-xl tracking-tight">{initials}</span>
              </div>
              <div>
                <h2 className="text-2xl font-extrabold text-[#1a1916] tracking-tight">{fullName}</h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {posName !== '-' && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#2e2d29]/8 text-[#2e2d29]">
                      <Star size={10} strokeWidth={2} /> {posName}
                    </span>
                  )}
                  {deptName !== '-' && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-[#e8dfd0] text-[#5a554e]">
                      <Building2 size={10} strokeWidth={1.8} /> {deptName}
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${emp.is_active ? 'bg-[#3d6e4e]/10 text-[#3d6e4e]' : 'bg-[#7a3535]/10 text-[#7a3535]'}`}>
                    <BadgeCheck size={10} strokeWidth={2} />
                    {emp.is_active ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
                {emp.hire_date && (
                  <p className="text-xs text-[#8a7e70] mt-1.5 flex items-center gap-1">
                    <Calendar size={11} strokeWidth={1.8} />
                    İşe giriş: {emp.hire_date}
                  </p>
                )}
              </div>
            </div>
            {/* Actions */}
            <div className="flex gap-2">
              {!editing ? (
                <Button size="sm" onClick={onEdit} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
                  <Pencil size={13} className="mr-1.5" /> Düzenle
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={onCancel} className="border-[#d0c4b0]">İptal</Button>
                  <Button size="sm" onClick={onSave} disabled={loading} className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e]">
                    <Save size={13} className="mr-1.5" /> {loading ? 'Kaydediliyor...' : 'Kaydet'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {editing ? (
        /* ── DÜZENLEME MODU ── */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Kişisel */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="pb-3 border-b border-[#e8dfd0]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#2e2d29]/8 flex items-center justify-center">
                  <User size={14} className="text-[#2e2d29]" strokeWidth={1.8} />
                </div>
                <CardTitle className="text-sm font-bold text-[#1a1916]">Kişisel Bilgiler</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ad" value={editForm.first_name} onChange={v => setEditForm(p => ({...p, first_name: v}))} />
                <Field label="Soyad" value={editForm.last_name} onChange={v => setEditForm(p => ({...p, last_name: v}))} />
              </div>
              <Field label="Kimlik No" value={editForm.national_id} onChange={v => setEditForm(p => ({...p, national_id: v}))} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Doğum Tarihi" value={editForm.birth_date} onChange={v => setEditForm(p => ({...p, birth_date: v}))} type="date" />
                <SelectField label="Cinsiyet" value={editForm.gender || ''} onChange={v => setEditForm(p => ({...p, gender: v || null}))}
                  options={[{value:'male',label:'Erkek'},{value:'female',label:'Kadın'}]} />
              </div>
            </CardContent>
          </Card>

          {/* İletişim */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="pb-3 border-b border-[#e8dfd0]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#3d6e4e]/8 flex items-center justify-center">
                  <Phone size={14} className="text-[#3d6e4e]" strokeWidth={1.8} />
                </div>
                <CardTitle className="text-sm font-bold text-[#1a1916]">İletişim</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Telefon" value={editForm.phone} onChange={v => setEditForm(p => ({...p, phone: v}))} type="tel" />
                <Field label="E-posta" value={editForm.email} onChange={v => setEditForm(p => ({...p, email: v}))} type="email" />
              </div>
              <Field label="Adres" value={editForm.address} onChange={v => setEditForm(p => ({...p, address: v}))} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Acil İletişim Adı" value={editForm.emergency_contact_name} onChange={v => setEditForm(p => ({...p, emergency_contact_name: v}))} />
                <Field label="Acil Tel" value={editForm.emergency_contact_phone} onChange={v => setEditForm(p => ({...p, emergency_contact_phone: v}))} type="tel" />
              </div>
            </CardContent>
          </Card>

          {/* İş Bilgileri */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="pb-3 border-b border-[#e8dfd0]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#7a6a2e]/8 flex items-center justify-center">
                  <Briefcase size={14} className="text-[#7a6a2e]" strokeWidth={1.8} />
                </div>
                <CardTitle className="text-sm font-bold text-[#1a1916]">İş Bilgileri</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <CreatableCombobox
                  label="Departman"
                  value={editForm.department_id || null}
                  onChange={v => setEditForm(p => ({...p, department_id: v, position_id: null}))}
                  options={departments.map(d => ({value: d.id, label: d.name}))}
                  placeholder="Ara veya yeni ekle..."
                  onCreateOption={createDepartment}
                />
                <CreatableCombobox
                  label="Pozisyon"
                  value={editForm.position_id || null}
                  onChange={v => setEditForm(p => ({...p, position_id: v}))}
                  options={positions
                    .filter(p => !editForm.department_id || p.department_id === editForm.department_id || !p.department_id)
                    .map(p => ({value: p.id, label: p.name}))}
                  placeholder="Ara veya yeni ekle..."
                  onCreateOption={createPosition}
                />
              </div>
              <SelectField label="Çalışma Türü" value={editForm.employment_type || 'full_time'} onChange={v => setEditForm(p => ({...p, employment_type: v}))}
                options={[{value:'full_time',label:'Tam Zamanlı'},{value:'part_time',label:'Yarı Zamanlı'},{value:'contractor',label:'Sözleşmeli'}]} />
              {/* Personelin ASIL merkezi. Puantajda otomatik gelir; ay içinde
                  geçici görev olursa puantajda gün gün değiştirilebilir. */}
              <MerkezSecici label="Sorumluluk Merkezi"
                value={editForm.cost_center_id || ''}
                onChange={v => setEditForm(p => ({...p, cost_center_id: v || null}))} />
              <div className="grid grid-cols-2 gap-3">
                <Field label={cokDonemli ? 'Son Dönem Girişi' : 'İşe Giriş'} value={editForm.hire_date} onChange={v => setEditForm(p => ({...p, hire_date: v}))} type="date" />
                <Field label="İşten Çıkış" value={editForm.termination_date || ''} onChange={v => setEditForm(p => ({...p, termination_date: v || null}))} type="date" />
              </div>
              {/* Bu iki alan SON donemi gunceller; eski donemler degismez */}
              {cokDonemli && (
                <p className="text-[11px] text-[#8a7e70] mt-2">
                  Bu alanlar <b>son hizmet dönemini</b> günceller; önceki dönemler olduğu gibi kalır.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Finansal */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="pb-3 border-b border-[#e8dfd0]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#2e6fa0]/8 flex items-center justify-center">
                  <CreditCard size={14} className="text-[#2e6fa0]" strokeWidth={1.8} />
                </div>
                <CardTitle className="text-sm font-bold text-[#1a1916]">Finansal Bilgiler</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <Field label="Sigorta Sicil No" value={editForm.insurance_number} onChange={v => setEditForm(p => ({...p, insurance_number: v}))} />
              <Field label="Banka" value={editForm.bank_name} onChange={v => setEditForm(p => ({...p, bank_name: v}))} />
              <Field label="Hesap No / IBAN" value={editForm.bank_account_number} onChange={v => setEditForm(p => ({...p, bank_account_number: v}))} />
            </CardContent>
          </Card>
        </div>
      ) : (
        /* ── GÖRÜNTÜLEME MODU ── */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Kişisel */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="pb-3 border-b border-[#e8dfd0]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#2e2d29]/8 flex items-center justify-center">
                  <User size={14} className="text-[#2e2d29]" strokeWidth={1.8} />
                </div>
                <CardTitle className="text-sm font-bold text-[#1a1916]">Kişisel Bilgiler</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <dl className="space-y-3">
                <InfoRow label="Kimlik No" value={emp.national_id} mono />
                <InfoRow label="Doğum Tarihi" value={emp.birth_date} />
                <InfoRow label="Cinsiyet" value={genders[emp.gender]} />
              </dl>
            </CardContent>
          </Card>

          {/* İletişim */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="pb-3 border-b border-[#e8dfd0]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#3d6e4e]/8 flex items-center justify-center">
                  <Phone size={14} className="text-[#3d6e4e]" strokeWidth={1.8} />
                </div>
                <CardTitle className="text-sm font-bold text-[#1a1916]">İletişim</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <dl className="space-y-3">
                <InfoRow label="Telefon" value={emp.phone} icon={<Phone size={13} className="text-[#8a7e70]" />}
                  ek={<WhatsAppDugmesi telefon={emp.phone} />} />
                <InfoRow label="E-posta" value={emp.email} icon={<Mail size={13} className="text-[#8a7e70]" />} />
                <InfoRow label="Adres" value={emp.address} icon={<MapPin size={13} className="text-[#8a7e70]" />} />
                {(emp.emergency_contact_name || emp.emergency_contact_phone) && (
                  <div className="pt-2 mt-2 border-t border-[#e8dfd0]">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#8a7e70] mb-2">Acil İletişim</p>
                    <div className="flex items-center gap-2 bg-[#7a3535]/5 rounded-lg px-3 py-2">
                      <AlertTriangle size={13} className="text-[#7a3535] flex-shrink-0" />
                      <div>
                        {emp.emergency_contact_name && <p className="text-sm font-semibold text-[#1a1916]">{emp.emergency_contact_name}</p>}
                        {emp.emergency_contact_phone && (
                          <p className="text-xs text-[#8a7e70] flex items-center gap-1">
                            {emp.emergency_contact_phone}
                            <WhatsAppDugmesi telefon={emp.emergency_contact_phone} />
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>

          {/* İş Bilgileri */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="pb-3 border-b border-[#e8dfd0]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#7a6a2e]/8 flex items-center justify-center">
                  <Briefcase size={14} className="text-[#7a6a2e]" strokeWidth={1.8} />
                </div>
                <CardTitle className="text-sm font-bold text-[#1a1916]">İş Bilgileri</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <dl className="space-y-3">
                <InfoRow label="Departman" value={deptName} />
                <InfoRow label="Pozisyon" value={posName} />
                <InfoRow label="Çalışma Türü" value={empTypes[emp.employment_type]} />
                <InfoRow label="Sorumluluk Merkezi"
                  value={(merkezler || []).find(m => m.id === emp.cost_center_id)?.name || '—'} />
                {/* Cok donemli personelde "Ise Giris" tek basina yaniltici:
                    ILK girisi gosteriyor, kidem ise son donemden sayiliyor. */}
                <InfoRow label={cokDonemli ? 'İlk İşe Giriş' : 'İşe Giriş'} value={emp.hire_date}
                  icon={<Calendar size={13} className="text-[#8a7e70]" />} />
                {cokDonemli && <InfoRow label="Kıdem Başlangıcı" value={emp.kidem_baslangici} />}
                <InfoRow label="İşten Çıkış" value={emp.termination_date}
                  emptyLabel={<span className="inline-flex items-center gap-1 text-xs font-semibold text-[#3d6e4e]"><Check size={12} /> Aktif çalışıyor</span>} />
              </dl>
              {/* Dokumun kendisi "Maas ve Donemler" sekmesinde; burada yalnizca isaret */}
              {cokDonemli && (
                <p className="mt-3 pt-3 border-t border-[#e8dfd0] text-[11px] text-[#8a7e70]">
                  Bu personelin <b>{(emp.donemler || []).length} hizmet dönemi</b> var;
                  dökümü <b>Maaş ve Dönemler</b> sekmesinde.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Finansal */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="pb-3 border-b border-[#e8dfd0]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#2e6fa0]/8 flex items-center justify-center">
                  <CreditCard size={14} className="text-[#2e6fa0]" strokeWidth={1.8} />
                </div>
                <CardTitle className="text-sm font-bold text-[#1a1916]">Finansal Bilgiler</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <dl className="space-y-3">
                <InfoRow label="Sigorta Sicil No" value={emp.insurance_number} mono icon={<Shield size={13} className="text-[#8a7e70]" />} />
                <InfoRow label="Banka" value={emp.bank_name} />
                <InfoRow label="Hesap No / IBAN" value={emp.bank_account_number} mono kopyala />
              </dl>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, mono, icon, emptyLabel, kopyala, ek }) {
  return (
    <div className="flex items-start justify-between gap-4 py-0.5">
      <dt className="text-xs font-semibold text-[#8a7e70] flex-shrink-0 min-w-[110px]">{label}</dt>
      <dd className={`text-sm text-right flex items-center gap-1.5 ${mono ? 'font-mono text-[#3F3E39]' : 'text-[#1a1916] font-medium'}`}>
        {icon && <span className="flex-shrink-0">{icon}</span>}
        {value ? value : (emptyLabel || <span className="text-[#8a7e70] font-normal">—</span>)}
        {value && ek}
        {/* Kopyalama yalnızca DEĞER VARSA: boş alanın yanında işlevsiz bir
            düğme durması kullanıcıya "kopyalanacak bir şey var" dedirtir */}
        {kopyala && value && (
          <button type="button"
            onClick={() => panoyaKopyala(value, `${label} kopyalandı`)}
            title={`${label} kopyala`} aria-label={`${label} kopyala`}
            className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md
                       text-[#8a7e70] hover:text-[#2e6fa0] hover:bg-[#2e6fa0]/10 transition-colors"
            data-testid={`kopyala-${label}`}>
            <Copy size={12} />
          </button>
        )}
      </dd>
    </div>
  );
}

// ── Helpers ──
function Info({ label, value, mono, span }) {
  return (
    <div className={span ? `md:col-span-${span}` : ''}>
      <p className="text-xs font-bold uppercase text-[#8a7e70]">{label}</p>
      <p className={`text-[#1a1916] ${mono ? 'font-mono' : ''}`}>{value || '-'}</p>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-[#8a7e70]">{label}</Label>
      <Input type={type} value={value || ''} onChange={e => onChange(e.target.value)} className="bg-white border-[#d0c4b0] h-9" />
    </div>
  );
}

// ── Creatable Combobox — arama + yeni ekleme ──
function CreatableCombobox({ label, value, onChange, options, placeholder = 'Ara veya ekle...', onCreateOption }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Seçili etiket
  const selectedLabel = options.find(o => o.value === value)?.label || '';

  // Dışarı tıklayınca kapat
  useEffect(() => {
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Açılınca input'u odakla
  useEffect(() => { if (open) { setQuery(''); inputRef.current?.focus(); } }, [open]);

  const filtered = options.filter(o => trNorm(o.label).includes(trNorm(query)));
  const exactMatch = options.some(o => trNorm(o.label) === trNorm(query));
  const showCreate = query.trim().length > 0 && !exactMatch;

  const handleSelect = (opt) => { onChange(opt.value); setOpen(false); setQuery(''); };

  const handleCreate = async () => {
    if (!query.trim() || creating) return;
    setCreating(true);
    try {
      const newOpt = await onCreateOption(query.trim());
      onChange(newOpt.value);
      setOpen(false);
      setQuery('');
    } catch { toast.error('Eklenemedi'); }
    setCreating(false);
  };

  const handleClear = (e) => { e.stopPropagation(); onChange(null); };

  return (
    <div className="space-y-1" ref={containerRef}>
      <Label className="text-xs text-[#8a7e70]">{label}</Label>
      <div className="relative">
        {/* Trigger */}
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="w-full h-9 px-3 pr-8 rounded-md border border-[#d0c4b0] bg-white text-sm text-left flex items-center justify-between transition-colors hover:border-[#8a7e70] focus:outline-none focus:ring-2 focus:ring-[#2e2d29]/15"
        >
          <span className={selectedLabel ? 'text-[#1a1916] font-medium' : 'text-[#8a7e70]'}>
            {selectedLabel || placeholder}
          </span>
          <div className="flex items-center gap-1">
            {value && (
              <span onClick={handleClear} className="text-[#8a7e70] hover:text-[#7a3535] p-0.5 rounded cursor-pointer">
                <X size={12} />
              </span>
            )}
            <ChevronDown size={13} className={`text-[#8a7e70] transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {/* Dropdown */}
        {open && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-[#d0c4b0] rounded-md shadow-lg overflow-hidden animate-fade-in">
            {/* Arama */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-[#e8dfd0]">
              <Search size={13} className="text-[#8a7e70] flex-shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Ara veya yeni ad yazın…"
                className="flex-1 text-sm outline-none bg-transparent text-[#1a1916] placeholder:text-[#8a7e70]"
                onKeyDown={e => { if (e.key === 'Escape') setOpen(false); if (e.key === 'Enter' && showCreate) handleCreate(); }}
              />
            </div>

            {/* Seçenekler */}
            <div className="max-h-48 overflow-y-auto">
              {filtered.length > 0 ? (
                filtered.map(o => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => handleSelect(o)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[#e8dfd0] transition-colors ${value === o.value ? 'bg-[#e8dfd0] font-semibold text-[#1a1916]' : 'text-[#3F3E39]'}`}
                  >
                    {o.label}
                    {value === o.value && <Check size={13} className="text-[#3d6e4e]" />}
                  </button>
                ))
              ) : (
                // Bos durum ne yapilacagini soylemeli (bkz. EmployeeList'teki ayni not)
                !showCreate && (
                  <p className="text-xs text-[#8a7e70] px-3 py-3 text-center">
                    {options.length === 0
                      ? 'Henüz kayıt yok — yeni eklemek için yukarıya yazın.'
                      : 'Sonuç yok — yeni eklemek için yazmaya devam edin.'}
                  </p>
                )
              )}

              {/* Yeni oluştur */}
              {showCreate && (
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating}
                  className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-[#3d6e4e] font-semibold hover:bg-[#3d6e4e]/8 transition-colors border-t border-[#e8dfd0]"
                >
                  <PlusCircle size={13} />
                  {creating ? 'Ekleniyor...' : `"${query.trim()}" ekle`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-[#8a7e70]">{label}</Label>
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value || null)}
        className="w-full h-9 px-3 rounded-md border border-[#d0c4b0] bg-white text-sm text-[#1a1916] focus:outline-none focus:ring-2 focus:ring-[#2e2d29]/20"
      >
        <option value="">Seçiniz</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function SimpleList({ title, data, columns }) {
  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0]"><CardTitle className="text-lg font-bold text-[#1a1916]">{title}</CardTitle></CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader><TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
            {columns.map(c => <TableHead key={c.key} className={`text-[#1a1916] font-bold text-xs uppercase ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {data.map((row, i) => (
              <TableRow key={row.id || i} className="border-[#d0c4b0]">
                {columns.map(c => (
                  <TableCell key={c.key} className={`text-sm ${c.align === 'right' ? 'text-right font-mono' : 'text-[#3F3E39]'}`}>
                    {c.render ? c.render(row[c.key], row) : (row[c.key] || '-')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {data.length === 0 && <TableRow><TableCell colSpan={columns.length} className="text-center py-6 text-[#8a7e70]">Kayit yok</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ── Document Tab ──
function DocTab({ empId, docs, onRefresh }) {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('employees', 'delete');
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ document_type: 'other', custom_label: '', expiry_date: '', notes: '' });
  const fileRef = useRef(null);
  const [fileData, setFileData] = useState(null);
  const [fileName, setFileName] = useState('');

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setFileData(ev.target.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try {
      await api.post(`/hr/employees/${empId}/documents`, { ...form, file_data: fileData, file_name: fileName });
      toast.success('Evrak yuklendi');
      setShow(false); setFileData(null); setFileName('');
      onRefresh();
    } catch { toast.error('Hata'); }
  };

  const del = async (docId) => {
    if (!window.confirm('Silmek istiyor musunuz?')) return;
    await api.delete(`/hr/employees/${empId}/documents/${docId}`);
    onRefresh();
  };

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-bold text-[#1a1916]">Evraklar</CardTitle>
        <Button size="sm" onClick={() => setShow(true)} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]"><Upload size={14} className="mr-1" /> Evrak Yukle</Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader><TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tur</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Dosya</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Yukleme</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Gecerlilik</TableHead>
            <TableHead className="w-10"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {docs.map(d => (
              <TableRow key={d.id} className="border-[#d0c4b0]">
                <TableCell><span className="status-badge bg-[#e8dfd0] text-[#3F3E39]">{docTypes[d.document_type] || d.custom_label || d.document_type}</span></TableCell>
                <TableCell className="text-sm text-[#1a1916]">{d.file_name || '-'}</TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{d.upload_date}</TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{d.expiry_date || '-'}</TableCell>
                <TableCell>{silmeYetkisi && <Button variant="ghost" size="icon" onClick={() => del(d.id)} className="h-7 w-7 text-[#8a7e70] hover:text-[#7a3535]"><Trash2 size={12} /></Button>}</TableCell>
              </TableRow>
            ))}
            {docs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-[#8a7e70]">Evrak yok</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Evrak Yukle</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Evrak Turu</Label>
              <Select value={form.document_type} onValueChange={v => setForm(p => ({...p, document_type: v}))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(docTypes).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-2"><Label>Gecerlilik Tarihi</Label>
              <Input type="date" value={form.expiry_date} onChange={e => setForm(p => ({...p, expiry_date: e.target.value}))} className="bg-white border-[#d0c4b0]" /></div>
            <div className="space-y-2"><Label>Dosya</Label>
              <div className="border-2 border-dashed border-[#d0c4b0] rounded-lg p-4 text-center cursor-pointer" onClick={() => fileRef.current?.click()}>
                {fileName ? <p className="text-sm text-[#1a1916]">{fileName}</p> : <p className="text-sm text-[#8a7e70]">Dosya sec</p>}
                <input ref={fileRef} type="file" onChange={handleFile} className="hidden" />
              </div></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)} className="border-[#d0c4b0]">Iptal</Button>
            <Button onClick={save} disabled={!fileData} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Yukle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── Maaş Sekmesi ──
// Maaş TL dışında da tanımlanabilir; TL karşılığı tahakkuk anında TCMB döviz
// alış kuruyla bulunur (hr.py `_tahakkuk_kuru`). Yol/yemek/telefon eskiden sabit
// üç TL alanıydı; yerine tür + tutar + para birimi satırları geldi.
const EMPTY_SAL = { effective_date: '', net_salary: '', currency: 'TRY', salary_type: 'monthly', ilave_haklar: [] };

const tutarBicim = (n) => (Number(n) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 });

function SalaryTab({ empId, salaries, onRefresh }) {
  const [show, setShow] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // salary record being edited
  const [form, setForm] = useState(EMPTY_SAL);
  const [secenek, setSecenek] = useState({ para_birimleri: ['TRY', 'EUR', 'USD', 'GBP'], ilave_hak_turleri: [], hesaplama_sekilleri: [] });
  const varsayilanHesap = (tur) => secenek.ilave_hak_turleri.find(t => t.kod === tur)?.varsayilan_hesaplama || 'aylik';
  const [kaydediyor, setKaydediyor] = useState(false);

  useEffect(() => {
    api.get('/hr/maas-secenekleri').then(r => setSecenek(r.data)).catch(() => {});
  }, []);
  const turAdi = (kod) => secenek.ilave_hak_turleri.find(t => t.kod === kod)?.ad || kod;

  const openNew = () => { setEditTarget(null); setForm(EMPTY_SAL); setShow(true); };
  const openEdit = (s) => {
    setEditTarget(s);
    setForm({
      effective_date: s.effective_date || '',
      net_salary: s.net_salary ?? '',
      currency: s.currency || 'TRY',
      salary_type: s.salary_type || 'monthly',
      ilave_haklar: (s.ilave_haklar || []).map(h => ({ ...h, tutar: String(h.tutar) })),
    });
    setShow(true);
  };

  const hakEkle = () => setForm(p => {
    // Henüz eklenmemiş ilk tür önerilir; para birimi maaşınkiyle başlar
    const kullanilan = new Set(p.ilave_haklar.map(h => h.tur));
    const tur = secenek.ilave_hak_turleri.find(t => !kullanilan.has(t.kod))?.kod || '';
    return { ...p, ilave_haklar: [...p.ilave_haklar, { tur, tutar: '', para_birimi: p.currency, hesaplama: varsayilanHesap(tur) }] };
  });
  // Tür değişince hesaplama şekli o türün varsayılanına döner (yol/yemek günlük,
  // diğerleri aylık); kullanıcı sonra yine değiştirebilir.
  const hakGuncelle = (i, alan, deger) => setForm(p => ({
    ...p, ilave_haklar: p.ilave_haklar.map((h, j) => (j !== i ? h
      : alan === 'tur' ? { ...h, tur: deger, hesaplama: varsayilanHesap(deger) } : { ...h, [alan]: deger })) }));
  const hakSil = (i) => setForm(p => ({ ...p, ilave_haklar: p.ilave_haklar.filter((_, j) => j !== i) }));

  const save = async () => {
    setKaydediyor(true);
    try {
      if (editTarget) {
        await api.put(`/hr/employees/${empId}/salary/${editTarget.id}`, form);
        toast.success('Maaş güncellendi');
      } else {
        await api.post(`/hr/employees/${empId}/salary`, form);
        toast.success('Maaş tanımlandı');
      }
      setShow(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally { setKaydediyor(false); }
  };

  const secimSinifi = 'h-10 px-2 rounded-md border border-[#d0c4b0] bg-white text-sm text-[#1a1916]';

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-bold text-[#1a1916]">Maaş Geçmişi</CardTitle>
        <Button size="sm" onClick={openNew} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]"><Plus size={14} className="mr-1" /> Yeni Maaş</Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader><TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Geçerlilik</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Net Maaş</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tip</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">İlave Haklar</TableHead>
            <TableHead className="w-10" />
          </TableRow></TableHeader>
          <TableBody>
            {salaries.map((s, i) => (
              <TableRow key={s.id} className="border-[#d0c4b0]">
                <TableCell className="text-sm text-[#3F3E39] whitespace-nowrap">{s.effective_date} {i === 0 && <span className="status-badge bg-[#3d6e4e]/10 text-[#3d6e4e] ml-2">Aktif</span>}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-[#1a1916] whitespace-nowrap">
                  {tutarBicim(s.net_salary)} <span className="text-xs text-[#8a7e70]">{s.currency || 'TRY'}</span>
                </TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{s.salary_type === 'monthly' ? 'Aylık' : s.salary_type === 'daily' ? 'Günlük' : 'Saatlik'}</TableCell>
                <TableCell className="text-sm text-[#3F3E39]">
                  {(s.ilave_haklar || []).length === 0 ? <span className="text-[#8a7e70]">—</span> : (
                    <div className="flex flex-wrap gap-1">
                      {s.ilave_haklar.map(h => (
                        <span key={h.tur} className="status-badge bg-[#2e2d29]/5 text-[#3F3E39]">
                          {turAdi(h.tur)} {tutarBicim(h.tutar)} {h.para_birimi}{h.hesaplama === 'gunluk' ? ' / gün' : ' / ay'}
                        </span>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)} className="h-7 w-7 p-0 text-[#8a7e70] hover:text-[#1a1916]">
                    <Pencil size={13} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {salaries.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-[#8a7e70]">Maaş tanımı yok</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-2xl">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">{editTarget ? 'Maaşı Düzenle' : 'Yeni Maaş Tanımı'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-[1fr_1.2fr_auto] gap-3 items-end">
              <div className="space-y-2"><Label>Geçerlilik *</Label>
                <Input type="date" value={form.effective_date} onChange={e => setForm(p => ({...p, effective_date: e.target.value}))} className="bg-white border-[#d0c4b0]" data-testid="maas-gecerlilik" /></div>
              <div className="space-y-2"><Label>Net Maaş *</Label>
                <Input type="number" step="any" min="0" value={form.net_salary} onChange={e => setForm(p => ({...p, net_salary: e.target.value}))} className="bg-white border-[#d0c4b0]" data-testid="maas-net" /></div>
              <div className="space-y-2"><Label className="block">Kur</Label>
                <select value={form.currency} onChange={e => setForm(p => ({...p, currency: e.target.value}))}
                  className={`${secimSinifi} w-24`} data-testid="maas-para-birimi">
                  {secenek.para_birimleri.map(k => <option key={k} value={k}>{k}</option>)}
                </select></div>
            </div>
            {form.currency !== 'TRY' && (
              <p className="text-[11px] text-[#8a7e70] -mt-2">
                Maaş {form.currency} olarak saklanır; tahakkukta ayın son gününden önceki iş gününün TCMB döviz alış kuruyla TL'ye çevrilir.
              </p>
            )}

            <div className="border-t border-[#d0c4b0] pt-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[#1a1916] font-medium">İlave Haklar</Label>
                <Button type="button" size="sm" variant="outline" onClick={hakEkle}
                  disabled={form.ilave_haklar.length >= secenek.ilave_hak_turleri.length && secenek.ilave_hak_turleri.length > 0}
                  className="h-8 border-[#d0c4b0]" data-testid="ilave-hak-ekle">
                  <Plus size={13} className="mr-1" /> İlave Hak Ekle
                </Button>
              </div>
              {form.ilave_haklar.length === 0 && (
                <p className="text-xs text-[#8a7e70]">Yol, yemek, telefon, gider ya da kira desteği varsa ekleyin.</p>
              )}
              {form.ilave_haklar.length > 0 && (
                <p className="text-[11px] text-[#8a7e70]">
                  Çalışılan gün başına: tutar günlüktür, izin ve resmi tatil günleri ödenmez. Aylık: izin ve resmi
                  tatilde de ödenir. Devamsızlık ve işten çıkış sonrası hiçbir hak ödenmez. Her hak cariye ayrı satır yazılır.
                </p>
              )}
              {form.ilave_haklar.map((h, i) => {
                const baskaSatirda = new Set(form.ilave_haklar.filter((_, j) => j !== i).map(x => x.tur));
                return (
                  <div key={i} className="grid grid-cols-[1fr_auto_1.2fr_1.3fr_auto] gap-2 items-center" data-testid={`ilave-hak-${i}`}>
                    <Input type="number" step="any" min="0" placeholder={h.hesaplama === 'gunluk' ? 'Günlük tutar' : 'Aylık tutar'} value={h.tutar}
                      onChange={e => hakGuncelle(i, 'tutar', e.target.value)} className="bg-white border-[#d0c4b0]" />
                    <select value={h.para_birimi} onChange={e => hakGuncelle(i, 'para_birimi', e.target.value)} className={`${secimSinifi} w-24`}>
                      {secenek.para_birimleri.map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                    <select value={h.tur} onChange={e => hakGuncelle(i, 'tur', e.target.value)} className={secimSinifi}>
                      <option value="">Tür seçin</option>
                      {secenek.ilave_hak_turleri.map(t => (
                        <option key={t.kod} value={t.kod} disabled={baskaSatirda.has(t.kod)}>{t.ad}</option>
                      ))}
                    </select>
                    <select value={h.hesaplama || 'aylik'} onChange={e => hakGuncelle(i, 'hesaplama', e.target.value)}
                      className={secimSinifi} title="Günlük: yalnızca çalışılan günler ödenir. Aylık: izin ve resmi tatilde de ödenir. Devamsızlıkta hiçbiri ödenmez."
                      data-testid={`ilave-hak-hesaplama-${i}`}>
                      {(secenek.hesaplama_sekilleri.length ? secenek.hesaplama_sekilleri
                        : [{ kod: 'gunluk', ad: 'Çalışılan gün başına' }, { kod: 'aylik', ad: 'Aylık' }]).map(x => (
                        <option key={x.kod} value={x.kod}>{x.ad}</option>
                      ))}
                    </select>
                    <Button type="button" size="sm" variant="ghost" onClick={() => hakSil(i)} title="Satırı sil" className="h-9 w-9 p-0">
                      <Trash2 size={14} className="text-[#a34b3f]" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={save} disabled={kaydediyor} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="maas-kaydet">
              {kaydediyor ? 'Kaydediliyor…' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── Leave Tab ──
// İzin sekmesi — İzinler sayfasıyla AYNI bileşenler (`izinOrtak.js`).
function IzinTab({ empId, emp }) {
  const { hasPermission } = useAuth();
  const onayYetkisi = hasPermission('timesheet', 'approve');
  const duzenleYetkisi = hasPermission('employees', 'edit');
  const [veri, setVeri] = useState(null);
  const [yeniAcik, setYeniAcik] = useState(false);

  const yukle = useCallback(() => {
    api.get(`/izin/personel/${empId}`).then(r => setVeri(r.data))
      .catch(() => toast.error('İzin bilgisi okunamadı'));
  }, [empId]);
  useEffect(() => { yukle(); }, [yukle]);

  if (!veri) return <p className="text-sm text-[#8a7e70] p-4">Yükleniyor…</p>;

  return (
    <div className="space-y-4">
      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardHeader className="pb-3 border-b border-[#e8dfd0]">
          <CardTitle className="text-sm font-bold text-[#1a1916]">Yıllık İzin</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <BakiyeOzeti bakiye={veri.bakiye} onayYetkisi={onayYetkisi} onDegisti={yukle} />
        </CardContent>
      </Card>
      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardHeader className="pb-3 border-b border-[#e8dfd0] flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-bold text-[#1a1916]">İzin Talepleri</CardTitle>
          {duzenleYetkisi && (
            <Button size="sm" onClick={() => setYeniAcik(true)} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
              <Plus size={14} className="mr-1" /> Yeni Talep
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <TalepTablosu talepler={veri.talepler} onayYetkisi={onayYetkisi} duzenleYetkisi={duzenleYetkisi}
            onDegisti={yukle} personelSutunu={false} />
        </CardContent>
      </Card>
      <YeniIzinDialog open={yeniAcik} onClose={() => setYeniAcik(false)} onKaydedildi={yukle}
        sabitPersonel={{ id: empId, ad: `${emp?.first_name || ''} ${emp?.last_name || ''}`.trim() }} />
    </div>
  );
}
